// src/utils/staffTimesheetPersistence.js
//
// Pass 83C — persistence for the ONE canonical `staffTimesheets` collection + the season-scoped
// `timesheetRequired` operator control. No payroll: nothing here reads or writes rates/wages/price. Source
// sheets are NEVER touched (Pass 82D). Timesheet docs are created only on adjust/submit (§17) — never eagerly.

import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db as defaultDb } from '../firebase';
import { timesheetWeekId, buildSubmissionSnapshot, timesheetTotals, validateTimesheetSubmission, SUBMIT_ERROR, TIMESHEET_STATUS } from './staffTimesheet';

export const STAFF_TIMESHEETS = 'staffTimesheets';

const str = (v) => String(v == null ? '' : v).trim();

export function normalizeTimesheetRecord(snapOrData, fallbackId = '') {
  const data = snapOrData && typeof snapOrData.data === 'function' ? (snapOrData.data() || {}) : (snapOrData || {});
  const id = (snapOrData && snapOrData.id) || data.id || fallbackId;
  return {
    id,
    organizationId: str(data.organizationId),
    memberUserId: str(data.memberUserId),
    memberAuthUserId: str(data.memberAuthUserId),
    weekStartKey: str(data.weekStartKey),
    weekEndKey: str(data.weekEndKey),
    timezone: str(data.timezone),
    status: data.status || TIMESHEET_STATUS.DRAFT,
    lines: Array.isArray(data.lines) ? data.lines : [],
    teachingMinutes: Number(data.teachingMinutes) || 0,
    otherMinutes: Number(data.otherMinutes) || 0,
    totalAttestedMinutes: Number(data.totalAttestedMinutes) || 0,
    revision: Number(data.revision) || 1,
    submittedByAuthUserId: str(data.submittedByAuthUserId),
    submittedAtIso: str(data.submittedAtIso),
    reopenedByAuthUserId: str(data.reopenedByAuthUserId),
    reopenReason: str(data.reopenReason),
    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,
    submittedAt: data.submittedAt || null,
    reopenedAt: data.reopenedAt || null,
  };
}

function identityFields({ organizationId, memberUserId, memberAuthUserId, week }) {
  return {
    organizationId: str(organizationId),
    memberUserId: str(memberUserId),
    memberAuthUserId: str(memberAuthUserId),
    weekStartKey: str(week.weekStartKey),
    weekEndKey: str(week.weekEndKey),
    timezone: str(week.timezone),
  };
}

export function timesheetDocId({ organizationId, memberUserId, week }) {
  return timesheetWeekId(organizationId, memberUserId, week && week.weekStartKey);
}

export async function loadTimesheet(db = defaultDb, id) {
  const snap = await getDoc(doc(db, STAFF_TIMESHEETS, id));
  return snap.exists() ? normalizeTimesheetRecord(snap, id) : null;
}

// Persist a DRAFT (only when the member adjusts/keeps manual lines) — merges identity + lines + totals.
// Pass 83C.1 §22/§23 — a REOPENED sheet stays REOPENED through edits (never silently reverted to draft); and
// createdAt is stamped only on first creation (never reset on subsequent saves).
export async function saveTimesheetDraft(db = defaultDb, { organizationId, memberUserId, memberAuthUserId, week, lines = [] } = {}) {
  const id = timesheetWeekId(organizationId, memberUserId, week && week.weekStartKey);
  if (!id) throw new Error('Timesheet identity incomplete');
  const ref = doc(db, STAFF_TIMESHEETS, id);
  const existing = await getDoc(ref);
  const prev = existing.exists() ? (existing.data() || {}) : null;
  // reopened edits remain reopened; a submitted sheet is not draft-editable (rules also enforce), so we only
  // ever write draft|reopened here.
  const status = prev && prev.status === TIMESHEET_STATUS.REOPENED ? TIMESHEET_STATUS.REOPENED : TIMESHEET_STATUS.DRAFT;
  const totals = timesheetTotals(lines);
  // Pass 83C.2 §3/§4 — memberAuthUserId is AUDIT METADATA (creator uid), NOT authorization truth (rules use the
  // LIVE canonical member.authUserId). Preserve the ORIGINAL on an existing sheet so an Auth relink does not
  // trip the rules' identity-immutability check; stamp the caller only on first create.
  const auditAuthUserId = prev ? str(prev.memberAuthUserId) : str(memberAuthUserId);
  const payload = {
    id, ...identityFields({ organizationId, memberUserId, memberAuthUserId: auditAuthUserId, week }),
    status, lines, ...totals, updatedAt: serverTimestamp(),
  };
  if (!prev) payload.createdAt = serverTimestamp();
  await setDoc(ref, payload, { merge: true });
  return id;
}

// Submit the week — freezes the snapshot (§31/§36). §16/§35 — refuses a NON-CLOSED week or unresolved lines at
// the domain layer (not just the UI). revision: first submit 1; each resubmit after a reopen increments.
export async function submitTimesheet(db = defaultDb, { organizationId, memberUserId, memberAuthUserId, week, lines = [], submittedByAuthUserId, now = new Date() } = {}) {
  const id = timesheetWeekId(organizationId, memberUserId, week && week.weekStartKey);
  if (!id) throw new Error('Timesheet identity incomplete');
  const check = validateTimesheetSubmission(lines, week, now);
  if (!check.ok) { const e = new Error(check.error === SUBMIT_ERROR.WEEK_OPEN ? 'This week is still in progress — you can submit it once it closes.' : 'Confirm every outstanding item before submitting.'); e.code = check.error; throw e; }
  const ref = doc(db, STAFF_TIMESHEETS, id);
  const existing = await getDoc(ref);
  const prev = existing.exists() ? (existing.data() || {}) : null;
  const revision = (Number(prev && prev.revision) || 0) + 1; // 1 on first submit; +1 each resubmit after reopen
  const snapshot = buildSubmissionSnapshot(lines, { revision });
  // §3/§4 — preserve the ORIGINAL creator audit uid; the CURRENT submitting Auth is recorded separately in
  // submittedByAuthUserId. This keeps identity immutable across an Auth relink while recording the real actor.
  const auditAuthUserId = prev ? str(prev.memberAuthUserId) : str(memberAuthUserId);
  const payload = {
    id, ...identityFields({ organizationId, memberUserId, memberAuthUserId: auditAuthUserId, week }),
    status: TIMESHEET_STATUS.SUBMITTED,
    lines: snapshot.lines,
    teachingMinutes: snapshot.teachingMinutes, otherMinutes: snapshot.otherMinutes, totalAttestedMinutes: snapshot.totalAttestedMinutes,
    revision,
    submittedByAuthUserId: str(submittedByAuthUserId),
    submittedAt: serverTimestamp(), submittedAtIso: new Date().toISOString(),
    updatedAt: serverTimestamp(),
  };
  if (!prev) payload.createdAt = serverTimestamp();
  await setDoc(ref, payload, { merge: true });
  return id;
}

// Operator reopen (§37) — flips submitted → reopened + audit ONLY, WITHOUT touching the frozen lines/minutes/
// revision/submit provenance (rules enforce the field allowlist).
export async function reopenTimesheet(db = defaultDb, { id, reason = '', reopenedByAuthUserId } = {}) {
  if (!id) throw new Error('Timesheet id required');
  await updateDoc(doc(db, STAFF_TIMESHEETS, id), {
    status: TIMESHEET_STATUS.REOPENED,
    reopenedAt: serverTimestamp(), reopenedByAuthUserId: str(reopenedByAuthUserId), reopenReason: str(reason),
    updatedAt: serverTimestamp(),
  });
}
